import React, { useState } from 'react';
import '../modules/Module.css';

const UserListElement = ({ user, linkedAgencies, availableAgencies, onExpand, onLink, onUnlink, onDelete }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedAgencyId, setSelectedAgencyId] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    const [unlinkingUid, setUnlinkingUid] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleToggleExpand = () => {
        if (!isExpanded) {
            onExpand(); // Fetch linked agencies when expanding
        }
        setIsExpanded(!isExpanded);
    };

    const linkedAgencyUids = new Set(linkedAgencies.map((a) => a.uid));
    const linkableAgencies = availableAgencies.filter((a) => !linkedAgencyUids.has(a.uid));

    const handleLinkClick = async (e) => {
        e.stopPropagation();
        if (!selectedAgencyId) return;
        setIsLinking(true);
        try {
            await onLink(selectedAgencyId);
            setSelectedAgencyId('');
        } finally {
            setIsLinking(false);
        }
    };

    const handleUnlinkClick = async (e, agencyUid) => {
        e.stopPropagation();
        setUnlinkingUid(agencyUid);
        try {
            await onUnlink(agencyUid);
        } finally {
            setUnlinkingUid(null);
        }
    };

    const handleDeleteClick = async (e) => {
        e.stopPropagation();
        const confirmed = window.confirm(
            `Are you sure you want to delete ${user.account_owner_name} (${user.email})? This will remove their login and all agency links. This action cannot be undone.`
        );
        if (!confirmed) return;

        setIsDeleting(true);
        try {
            await onDelete();
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="user-list-element">
            <div className="user-header" onClick={handleToggleExpand}>
                <div className="user-primary-info">
                    <div className="user-name-section">
                        <h3 className="user-list-name">{user.account_owner_name}</h3>
                        <span className="user-list-email">{user.email}</span>
                    </div>
                </div>
                <div className="agency-stats">
                    <div className="stat-item">
                        <span className="stat-label">Agencies:</span>
                        <span className="stat-value">{user.agency_count || 0}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">Last Login:</span>
                        <span className="stat-value">{formatDate(user.last_login_timestamp)}</span>
                    </div>
                </div>
                <button className="expand-button">
                    <i className={`fa-solid fa-angle-${isExpanded ? 'up' : 'down'}`}></i>
                </button>
            </div>

            {isExpanded && (
                <div className="users-section">
                    <div className="users-header">
                        <h4>User Details</h4>
                    </div>
                    <div className="agency-details-expanded">
                        <div className="detail-row">
                            <div className="detail-item">
                                <span className="detail-label">Registered:</span>
                                <span className="detail-value">{formatDate(user.registered_timestamp)}</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Last Login:</span>
                                <span className="detail-value">{formatDate(user.last_login_timestamp)}</span>
                            </div>
                        </div>
                        <div className="detail-row">
                            <div className="detail-item">
                                <span className="detail-label">User ID:</span>
                                <span className="detail-value">{user.uid}</span>
                            </div>
                            <div className="detail-item">
                                <span className="detail-label">Agency Name (legacy field):</span>
                                <span className="detail-value">{user.agency_name || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    <div className="agencies-list-section">
                        <div className="users-list-header">
                            <h4>Linked Agencies ({linkedAgencies.length})</h4>
                        </div>
                        {linkedAgencies.length > 0 ? (
                            <div className="agencies-list">
                                {linkedAgencies.map((agency) => (
                                    <div key={agency.uid} className="agency-item">
                                        <div className="agency-item-info">
                                            <div className="agency-item-name">
                                                {agency.agency_name || `Agency ${agency.uid}`} ({agency.agency_number})
                                            </div>
                                            <div className="agency-item-role">
                                                {agency.user_role_description || 'No role specified'}
                                            </div>
                                        </div>
                                        <button
                                            className="remove-button"
                                            disabled={unlinkingUid === agency.uid}
                                            onClick={(e) => handleUnlinkClick(e, agency.uid)}
                                        >
                                            {unlinkingUid === agency.uid ? 'Removing...' : 'Remove'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="agencies-list-empty">No agencies linked yet.</div>
                        )}

                        <div className="add-link-row" onClick={(e) => e.stopPropagation()}>
                            <select
                                value={selectedAgencyId}
                                onChange={(e) => setSelectedAgencyId(e.target.value)}
                                className="standard-input"
                            >
                                <option value="">Select an agency to link</option>
                                {linkableAgencies.map((agency) => (
                                    <option key={agency.uid} value={agency.uid}>
                                        {agency.agency_name || `Agency ${agency.uid}`} ({agency.agency_number})
                                    </option>
                                ))}
                            </select>
                            <button
                                className="link-button"
                                disabled={!selectedAgencyId || isLinking}
                                onClick={handleLinkClick}
                            >
                                {isLinking ? 'Linking...' : 'Link'}
                            </button>
                        </div>
                    </div>

                    <div className="danger-zone">
                        <button
                            className="remove-button delete-user-button"
                            disabled={isDeleting}
                            onClick={handleDeleteClick}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete User'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserListElement;
